import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

function CategoryShow() {
    const { id } = useParams();
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadCategory();
    }, [id]);

    const loadCategory = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/categories/${id}`);
            if (response.success) {
                setCategory(response.data);
            } else {
                setError(response.message || 'Failed to load category');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Loading category...</div>;
    if (error) return <div className="mt-16 p-8 text-center text-red-500 font-medium">{error}</div>;
    if (!category) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Category not found</div>;

    return (
        <div className="mt-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <Link to="/categories" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-800">{category.name}</h1>
                    </div>
                    <p className="text-slate-500 mt-1 ml-11">{category.description || 'No description provided'}</p>
                </div>
                <div className="flex items-center gap-3 ml-11 sm:ml-0">
                    <Link to={`/categories/${category.id}/edit`} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors shadow-sm text-sm">
                        Edit Category
                    </Link>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-800">Products in this Category</h2>
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-bold uppercase tracking-widest">
                        {category.products?.length || 0} Items
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">SKU</th>
                                <th className="px-6 py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">Stock</th>
                                <th className="px-6 py-3 text-right text-xs font-bold text-slate-400 uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {category.products?.map(product => (
                                <tr key={product.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 font-medium text-slate-800">{product.name}</td>
                                    <td className="px-6 py-4 text-slate-500 font-mono text-sm">{product.sku || '-'}</td>
                                    <td className="px-6 py-4 text-center font-bold text-slate-800">{product.stock_quantity}</td>
                                    <td className="px-6 py-4 text-right">
                                        <Link to={`/products/${product.id}`} className="text-indigo-600 hover:text-indigo-800 font-medium text-sm">
                                            View
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                            {(!category.products || category.products.length === 0) && (
                                <tr>
                                    <td colSpan="4" className="px-6 py-12 text-center text-slate-400 font-medium italic">
                                        No products assigned to this category
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default CategoryShow;
